import { useMemo } from 'react';

import type { EnterpriseProperties } from '@coursera/event-pulse-types';

import { useProgramMembershipsAndInvitations } from 'bundles/unified-common/contexts/org-scoped/EnterpriseOrgUserExperienceContext';
import {
  useOrgSlug,
  useThirdPartyOrganization,
} from 'bundles/unified-common/contexts/org-scoped/ThirdPartyOrganizationContext';

/**
 * Event-data block we attach to V3 events on unified discovery surfaces
 * (browse, home, my-learning, skillsets, etc.).
 *
 * On enterprise org-scoped routes the hook fills in the `enterprise` block
 * with organizationSlug, organizationId, and programIds. On consumer routes
 * it returns `{}`, leaving event data unchanged. This is also the natural
 * home for any consumer-specific event properties we add later — keep call
 * sites pointing here rather than splitting consumer/enterprise hooks.
 */
const useUnifiedDiscoveryTrackingProperties = (): { enterprise?: EnterpriseProperties } => {
  const thirdPartyOrganization = useThirdPartyOrganization();
  const orgSlug = useOrgSlug();
  const programMemberships = useProgramMembershipsAndInvitations();

  return useMemo(() => {
    const organizationId = thirdPartyOrganization?.id;
    const programIds = programMemberships?.map((edge) => edge?.node?.program?.id).filter(Boolean) ?? [];

    if (!organizationId && !orgSlug && programIds.length === 0) {
      return {};
    }

    return {
      enterprise: {
        organizationSlug: orgSlug,
        organizationId,
        programIds,
      },
    };
  }, [thirdPartyOrganization?.id, orgSlug, programMemberships]);
};

export default useUnifiedDiscoveryTrackingProperties;
